if (!customElements.get("tabbed-content")) {
  class Tabs extends HTMLElement {
    constructor() {
      super();
      this.tabList = this.querySelector('[role="tablist"]');
      this.layoutMode = this.dataset.layout || "tabs"; // 'tabs' or 'sections'
      this.activeTab = this.tabList
        ? this.tabList.querySelector('[aria-selected="true"]')
        : null;
      this.isVerticalTablist = this.tabList
        ? this.tabList.getAttribute("aria-orientation") === "vertical"
        : false;
      this.tabs = this.querySelectorAll('[role="tab"]');
      this.panels = this.querySelectorAll('[role="tabpanel"]');

      if (this.layoutMode === "sections") {
        // In sections mode, clear all active states initially
        // The active tab will be determined by scroll position
        this.tabs.forEach((tab) => {
          tab.setAttribute("aria-selected", "false");
          const container = tab.closest('.tablist__tab-container');
          if (container) {
            container.classList.remove('tablist__tab-container--active');
          }
        });
        this.initSectionsMode();
      } else {
        // If no tab is active by default, activate the first tab.
        if (!this.activeTab && this.tabs.length > 0) {
          this.activeTab = this.tabs[0];
          this.activateTab(this.activeTab);
        }
        this.addListeners();
        // Initialize container classes for active tabs (tabs mode only)
        this.initializeContainerClasses();
      }

      // Setup dynamic positioning for sticky tablist
      this.setupDynamicPositioning();

      // Handle anchor links to tabs
      this.handleAnchorLinks();
    }

    /**
     * Initialize container classes based on current tab states
     */
    initializeContainerClasses() {
      // First, remove active class from all containers
      const allContainers = this.querySelectorAll('.tablist__tab-container');
      allContainers.forEach((container) => {
        container.classList.remove('tablist__tab-container--active');
      });

      // Then, add active class only to the container with the active tab
      this.tabs.forEach((tab) => {
        const container = tab.closest('.tablist__tab-container');
        if (container && tab.getAttribute('aria-selected') === 'true') {
          container.classList.add('tablist__tab-container--active');
        }
      });
    }

    initSectionsMode() {
      this.panels.forEach((panel) => {
        panel.hidden = false;
      });
      this.tabs.forEach((tab) => {
        tab.addEventListener("click", (e) => {
          e.preventDefault();
          this.scrollToSection(tab);
        });
      });

      this.setupActiveSectionTracking();
    }

    scrollToSection(tab) {
      const panelId = tab.getAttribute("aria-controls");
      const panel = document.getElementById(panelId);
      if (!panel) return;

      // Calculate header offset
      const headerHeight = Number.parseInt(
        getComputedStyle(document.documentElement)
          .getPropertyValue("--header-height")
          .replace("px", "") || "0",
        10
      );
      
      // Get tablist height if sticky
      const stickyTablist = this.querySelector('.tablist--sticky');
      const tablistHeight = stickyTablist ? stickyTablist.getBoundingClientRect().height : 60;
      
      // Calculate offset: header + tab bar + extra padding
      const offset = headerHeight + tablistHeight + 20;

      // Try to find the h2 heading first, otherwise use panel
      const heading = panel.querySelector('h2');
      const targetElement = heading || panel;
      
      const elementTop = targetElement.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementTop - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });

      this.updateActiveTab(tab);
    }

    /**
     * Handles anchor links that point to tabs (e.g., #tab-1)
     */
    handleAnchorLinks() {
      // Check if there's a hash in the URL pointing to a tab
      const checkHash = () => {
        const hash = window.location.hash;
        if (!hash) return;

        // Check if hash points to a tab button
        const tab = document.querySelector(hash);
        if (tab && tab.matches('[role="tab"]')) {
          // Use our scroll function instead of browser default
          setTimeout(() => {
            this.scrollToSection(tab);
          }, 100);
        }
      };

      // Check on load
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', checkHash);
      } else {
        checkHash();
      }

      // Handle clicks on links that point to tabs
      document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href^="#tab-"]');
        if (link) {
          const hash = link.getAttribute('href');
          const tab = document.querySelector(hash);
          if (tab && tab.matches('[role="tab"]') && Array.from(this.tabs).includes(tab)) {
            e.preventDefault();
            this.scrollToSection(tab);
            // Update URL without scrolling
            history.pushState(null, '', hash);
          }
        }
      });
    }

    updateActiveTab(tab) {
      // Remove active state from all tabs and their containers
      this.tabs.forEach((t) => {
        t.setAttribute("aria-selected", "false");
        t.classList.remove("tablist__tab--active");
        const container = t.closest('.tablist__tab-container');
        if (container) {
          container.classList.remove('tablist__tab-container--active');
        }
      });
      
      // Set active state on selected tab and its container
      tab.setAttribute("aria-selected", "true");
      tab.classList.add("tablist__tab--active");
      const activeContainer = tab.closest('.tablist__tab-container');
      if (activeContainer) {
        activeContainer.classList.add('tablist__tab-container--active');
      }
    }

    setupActiveSectionTracking() {
      if (!("IntersectionObserver" in window)) return;

      const headerHeight = Number.parseInt(
        getComputedStyle(document.documentElement)
          .getPropertyValue("--header-height")
          .replace("px", "") || "0",
        10
      );
      const offset = headerHeight + 100;

      // Store intersection ratios for ALL panels, not just changed entries
      this.panelIntersections = new Map();

      this.observer = new IntersectionObserver(
        (entries) => {
          // Update stored ratios for changed entries
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.panelIntersections.set(entry.target, entry.intersectionRatio);
            } else {
              this.panelIntersections.delete(entry.target);
            }
          });

          // Find the panel with the highest intersection ratio among ALL panels
          let maxRatio = 0;
          let activePanel = null;

          this.panelIntersections.forEach((ratio, panel) => {
            if (ratio > maxRatio) {
              maxRatio = ratio;
              activePanel = panel;
            }
          });

          if (activePanel) {
            const panelId = activePanel.id;
            const correspondingTab = [...this.tabs].find(
              (tab) => tab.getAttribute("aria-controls") === panelId
            );
            if (correspondingTab) {
              this.updateActiveTab(correspondingTab);
            }
          }
        },
        {
          rootMargin: `-${offset}px 0px -50% 0px`,
          threshold: [0, 0.1, 0.5, 1],
        }
      );

      this.panels.forEach((panel) => {
        this.observer.observe(panel);
      });
    }

    addListeners() {
      this.tabList.addEventListener("click", this.handleClick.bind(this));
      this.tabList.addEventListener("keydown", this.handleKeydown.bind(this));
    }

    /**
     * Handles 'click' events on the tablist.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('[role="tab"]') || evt.target === this.activeTab)
        return;
      this.activateTab(evt.target);
    }

    /**
     * Handles 'keydown' events on the tablist.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      switch (evt.key) {
        case "ArrowLeft":
        case "ArrowRight":
          evt.preventDefault();
          if (!this.isVerticalTablist) {
            this.switchTabOnKeyPress(evt.key);
          }
          break;

        case "ArrowUp":
        case "ArrowDown":
          evt.preventDefault();
          if (this.isVerticalTablist) {
            this.switchTabOnKeyPress(evt.key);
          }
          break;

        // Activate the first tab.
        case "Home":
          evt.preventDefault();
          this.activateTab(this.tabs[0]);
          break;

        // Activate the last tab.
        case "End":
          evt.preventDefault();
          this.activateTab(this.tabs[this.tabs.length - 1]);
          break;
      }
    }

    /**
     * Activates the next or previous tab according to the key pressed.
     * @param {string} key - Key pressed.
     */
    switchTabOnKeyPress(key) {
      if (key === "ArrowRight" || key === "ArrowDown") {
        // If active tab is the last tab.
        if (this.activeTab === this.tabs[this.tabs.length - 1]) {
          // Activate first tab.
          this.activateTab(this.tabs[0]);
        } else {
          // Activate next tab.
          this.activateTab(this.activeTab.nextElementSibling);
        }
      } else if (key === "ArrowLeft" || key === "ArrowUp") {
        // If active tab is the first tab.
        if (this.activeTab === this.tabs[0]) {
          this.activateTab(this.tabs[this.tabs.length - 1]);
        } else {
          // Activate the previous tab.
          this.activateTab(this.activeTab.previousElementSibling);
        }
      }
    }

    /**
     * Activates a tab.
     * @param {Element} tab - Tab element (button).
     */
    activateTab(tab) {
      this.deactivateActiveTab();

      Tabs.setTabState(tab, true);
      tab.removeAttribute("tabindex");
      this.activeTab = tab;

      if (document.activeElement.matches(".tablist__tab")) {
        tab.focus();
      }
    }

    /**
     * Deactivates the currently active tab.
     */
    deactivateActiveTab() {
      Tabs.setTabState(this.activeTab, false);
      this.activeTab.setAttribute("tabindex", "-1");
      this.activeTab = null;
    }

    /**
     * Sets the active state of a tab and associated content panel.
     * @param {Element} tab - Tab element (button).
     * @param {boolean} active - Set the tab as active.
     */
    static setTabState(tab, active) {
      tab.setAttribute("aria-selected", active);

      // Update container class for underline styling
      const container = tab.closest('.tablist__tab-container');
      if (container) {
        if (active) {
          container.classList.add('tablist__tab-container--active');
        } else {
          container.classList.remove('tablist__tab-container--active');
        }
      }

      const panelId = tab.getAttribute("aria-controls");
      const panel = document.getElementById(panelId);
      if (panel) {
        panel.hidden = !active;
      }
    }

    /**
     * Sets up dynamic positioning for sticky tablist based on header collapse state
     */
    setupDynamicPositioning() {
      const stickyTablist = this.querySelector('.tablist--sticky');
      if (!stickyTablist) return;

      // Try to find header - could be store-header element or .header class
      const header = document.querySelector('store-header.header') || 
                     document.querySelector('.header') ||
                     document.querySelector('store-header');
      if (!header) return;

      // Store the expanded header height for comparison
      let expandedHeaderHeight = 0;
      
      const updateTablistPosition = () => {
        const isOut = header.classList.contains('is-out');
        const currentHeaderHeight = header.getBoundingClientRect().height;
        
        // When header is expanded, store its height
        if (!isOut) {
          expandedHeaderHeight = currentHeaderHeight;
          stickyTablist.style.setProperty('--tablist-offset', '0px');
          return;
        }
        
        // When collapsed, calculate the difference
        if (expandedHeaderHeight > 0) {
          const heightDifference = expandedHeaderHeight - currentHeaderHeight;
          if (heightDifference > 0) {
            stickyTablist.style.setProperty('--tablist-offset', `${heightDifference}px`);
          } else {
            // Fallback to menu-height if calculation fails
            let menuHeight = getComputedStyle(header)
              .getPropertyValue('--menu-height')
              .trim();
            if (!menuHeight || menuHeight === '') {
              menuHeight = getComputedStyle(document.documentElement)
                .getPropertyValue('--menu-height')
                .trim() || '0px';
            }
            stickyTablist.style.setProperty('--tablist-offset', menuHeight);
          }
        } else {
          // If we don't have expanded height yet, use menu-height as fallback
          let menuHeight = getComputedStyle(header)
            .getPropertyValue('--menu-height')
            .trim();
          if (!menuHeight || menuHeight === '') {
            menuHeight = getComputedStyle(document.documentElement)
              .getPropertyValue('--menu-height')
              .trim() || '0px';
          }
          stickyTablist.style.setProperty('--tablist-offset', menuHeight);
        }
      };

      // Initialize expanded height and set initial position
      const initializePosition = () => {
        // If header is not collapsed, store its expanded height
        if (!header.classList.contains('is-out')) {
          expandedHeaderHeight = header.getBoundingClientRect().height;
        }
        updateTablistPosition();
      };
      
      // Wait a bit for header to initialize
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          setTimeout(initializePosition, 200);
        });
      } else {
        setTimeout(initializePosition, 200);
      }

      // Watch for header class changes
      const headerObserver = new MutationObserver(() => {
        updateTablistPosition();
      });

      headerObserver.observe(header, {
        attributes: true,
        attributeFilter: ['class']
      });

      // Also listen to scroll events to catch rapid state changes
      let scrollTimeout;
      const scrollHandler = () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          updateTablistPosition();
        }, 50);
      };
      
      window.addEventListener('scroll', scrollHandler, { passive: true });

      // Store observer and handler for cleanup
      this.headerObserver = headerObserver;
      this.scrollHandler = scrollHandler;
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.headerObserver) {
        this.headerObserver.disconnect();
      }
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
      }
      if (this.panelIntersections) {
        this.panelIntersections.clear();
      }
    }
  }

  customElements.define("tabbed-content", Tabs);
}
